import React from 'react'

import { Moment } from 'moment'
import * as R from 'ramda'
import { useSelector } from 'react-redux'
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom'

import { Playbook } from '@driftt/api-types'
import { getPlaybookEditorUrlPath } from '@driftt/playbook-components/dist/urlPathUtils'
import {
  Breadcrumbs,
  IconButton,
  NavigationHeader,
  NavigationHeaderNavProps,
  ToolTip,
} from '@driftt/tide-core'

import { EmailCampaign } from 'api/email/types'
import DateRangePicker from 'components/filters/DateRangePicker'
import config from 'config'
import {
  userCanEditBotPlaybooks,
  userCanEditEmailCampaigns,
} from 'utils/access'
import { openExternalURL } from 'utils/url'

interface IDateOptions {
  showQuickSelects: boolean
  earliestDate?: Moment
  description?: string
}

export interface ISection {
  showDatePicker: boolean
  path: (queryParams: { [key: string]: string }) => string
  title: (
    queryParams: { [key: string]: string },
    context: { playbook?: Playbook; emailCampaign?: EmailCampaign }
  ) => string
  component: React.ReactNode
  hideBreadcrumb?: boolean
  hideHeader?: boolean
  dateOptions?: IDateOptions
  isHidden?: () => boolean
  exact?: boolean
  external?: boolean
  predicate?: (queryParams: { [key: string]: string }) => boolean
  subSections?: ISection[]
}
export interface ISectionSpec {
  sections: ISection[]
  basePath: string
  baseTitle: string
}

const SectionHeader: React.FC<ISectionSpec> = ({
  sections,
  basePath,
  baseTitle,
  children,
}) => {
  const location = useLocation()
  const history = useHistory()
  const params = useParams<{ playbookId: string; emailCampaignId: string }>()
  const match = useRouteMatch()

  const playbook = useSelector<any, Playbook | undefined>(
    R.path(['__reports-web', 'playbooks', params.playbookId])
  )
  const emailCampaign = useSelector<any, EmailCampaign | undefined>(
    R.path(['__reports-web', 'emailCampaigns', params.emailCampaignId])
  )

  const activeBaseSection = sections.find((section) => {
    const matchingParam = params[section.path(params).replace(':', '')] || false
    return matchingParam
      ? location.pathname.includes(matchingParam)
      : location.pathname.includes(section.path(params))
  })

  const activeSubSection =
    activeBaseSection &&
    activeBaseSection.subSections &&
    activeBaseSection.subSections.length &&
    activeBaseSection.subSections.find((section) => {
      const matchingParam =
        params[section.path(params).replace(':', '')] || false
      return matchingParam
        ? location.pathname.includes(matchingParam)
        : location.pathname.includes(section.path(params))
    })

  const activeSection = activeSubSection || activeBaseSection || null

  const showDateRangePicker = activeSection?.showDatePicker
  const dateOptions = (activeSection && activeSection.dateOptions) || {}

  const canEditPlaybook = !!playbook && userCanEditBotPlaybooks()
  const handleEditPlaybookClick = (event) => {
    openExternalURL(getPlaybookEditorUrlPath(playbook), event)
  }

  const canEditEmailCampaign = !!emailCampaign && userCanEditEmailCampaigns()
  const handleEditEmailCampaignClick = (event) => {
    openExternalURL(
      `${config.APP_URL}/email/campaigns/editor/${emailCampaign?.id}`,
      event
    )
  }

  const generateBreadCrumbs = () => {
    const currentPathCrumb =
      activeSection && !activeSection?.hideBreadcrumb
        ? [
            {
              title: activeSection.title(params, { playbook, emailCampaign }),
              link: `/${basePath}/${activeSection.path(params)}${
                history.location.search
              }`,
              isActive: true,
            },
          ]
        : []

    const headings = [
      {
        title: 'Reports',
        link: '/',
      },
      {
        title: baseTitle,
        link: `/reports/${basePath}`,
      },
      ...currentPathCrumb,
    ].filter(Boolean)

    return <Breadcrumbs headings={headings} />
  }

  const subSectionNav = (): NavigationHeaderNavProps[] | undefined => {
    if (!activeBaseSection || !activeBaseSection.subSections) return undefined
    return activeBaseSection.subSections
      .filter((subSection) => !subSection.isHidden || !subSection.isHidden())
      .filter(
        (subSection) => !subSection.predicate || subSection.predicate(params)
      )
      .map((subSection) => {
        return {
          route: subSection.external
            ? ''
            : `${match.url}/${subSection.path(params)}`,
          label: subSection.title(params, { playbook, emailCampaign }),
          onClick: () => {
            if (subSection.external) {
              window.open(subSection.path(params), '_blank')
            } else {
              history.push(
                `${match.url}/${subSection.path(params)}${
                  history.location.search
                }`
              )
            }
          },
        }
      })
  }

  if (activeBaseSection?.hideHeader) return null
  return (
    <>
      <NavigationHeader
        title={
          activeBaseSection
            ? activeBaseSection.title(params, { playbook, emailCampaign })
            : ''
        }
        secondary={generateBreadCrumbs()}
        history={history}
        nav={subSectionNav()}
      >
        {showDateRangePicker && (
          <DateRangePicker anchorDirection="right" {...dateOptions} />
        )}
        {canEditPlaybook && (
          <ToolTip render={() => 'Go to playbook'} flow="left">
            <IconButton icon="bot-2" onClick={handleEditPlaybookClick} />
          </ToolTip>
        )}
        {canEditEmailCampaign && (
          <ToolTip render={() => 'Go to email campaign'} flow="left">
            <IconButton
              icon="email-send"
              onClick={handleEditEmailCampaignClick}
            />
          </ToolTip>
        )}
        {children}
      </NavigationHeader>
    </>
  )
}

export default SectionHeader
